import { CheckboxBase } from '@orn/shared-web/components/Checkbox/Checkbox';
import * as React from 'react';

import { ChecklistSection } from '../../__generated__/globalTypes';

import InlineMarkdown from './components/InlineMarkdown';
import { checklistKey, computeReleaseChecklistGateState, type IReleaseChecklistGateState } from './releaseLogic';

interface IReleaseChecklistGateProps {
  /**
   * Literal post-`- [ ] ` content of each prerequisite line in the
   * draft body (markers stripped). Sourced from the body — not the live
   * LLM preview, which paraphrases as `main` advances. Each entry is
   * rendered as inline markdown and hashed for the toggle round-trip.
   */
  prerequisiteLineContents: string[];
  /** Same as `prerequisiteLineContents`, for the test-plan section. */
  testPlanLineContents: string[];
  /**
   * Indices of currently-checked prerequisite items, derived from the
   * body's `- [x]` markers. Drives the persistent / cross-tab visible
   * state of the gate.
   */
  prerequisiteCheckedIndices: number[];
  /** Same as `prerequisiteCheckedIndices`, for the test-plan section. */
  testPlanCheckedIndices: number[];
  /**
   * Parallel to `prerequisiteLineContents`. Marker text (e.g.
   * `Checked by **neville** at 5/6/2026 @ 6:59pm`) for the line at each
   * position; empty string when the item has no marker yet.
   */
  prerequisiteCheckedMarkers: string[];
  /** Same as `prerequisiteCheckedMarkers`, for the test-plan section. */
  testPlanCheckedMarkers: string[];
  /**
   * Notified whenever the gate's aggregate state changes. The parent
   * uses this to enable/disable the Publish button and surface the
   * next-unchecked label in a tooltip.
   */
  onStateChange: (state: IReleaseChecklistGateState) => void;
  /**
   * Fired when the operator toggles a checkbox. The parent fires the
   * `adminToggleReleaseChecklistItem` mutation. `lineContent` is the
   * exact string the gate hashed — the parent re-hashes it and sends
   * the result so the server can detect drift via `STALE_CHECKLIST`.
   */
  onToggle: (section: ChecklistSection, index: number, checked: boolean, lineContent: string) => void;
}

interface IRowProps {
  lineContent: string;
  marker: string;
  isChecked: boolean;
  isTestPlan: boolean;
  onToggle: (next: boolean) => void;
}

/**
 * One checklist row. The wrapper is a clickable `<div>` (not a `<label>`):
 * clicks on inline-markdown anchors must navigate without toggling the
 * box, and a label's input-association forwards every descendant click to
 * the input via the click event's default action — calling
 * `preventDefault` to suppress that also cancels the anchor's navigation
 * (the default action propagates up the entire bubble chain). With a
 * plain `<div>`, anchor clicks navigate via their own default and we
 * fire the toggle from `onClick` only when the target isn't an anchor.
 *
 * The `<input>` is `sr-only` so keyboard users can still tab to it and
 * toggle with space; `CheckboxBase` is the purely-presentational widget
 * tied to `isChecked` via React state.
 *
 * Test-plan items are formatted as `**action** → result`; we split on
 * the first ` → ` so the result lands on its own indented line beneath
 * the action — matches how the test plan reads in the GitHub markdown.
 */
const ChecklistRow = ({ lineContent, marker, isChecked, isTestPlan, onToggle }: IRowProps) => {
  const { primary, secondary } = React.useMemo(() => {
    if (!isTestPlan) {
      return { primary: lineContent, secondary: null };
    }
    // The LLM emits `**action** → result`; the action itself can contain
    // ` → ` (e.g. "Spending → Home"), so splitting on the first ` → `
    // gets the wrong boundary. Split on `** → ` — closing bold + arrow —
    // which only appears at the action/result seam. Fall back to the
    // last ` → ` for legacy items written without surrounding bold.
    const boldArrowIndex = lineContent.lastIndexOf('** → ');
    if (boldArrowIndex !== -1) {
      return {
        primary: lineContent.slice(0, boldArrowIndex + '**'.length),
        secondary: lineContent.slice(boldArrowIndex + '** → '.length),
      };
    }
    const lastArrowIndex = lineContent.lastIndexOf(' → ');
    if (lastArrowIndex === -1) {
      return { primary: lineContent, secondary: null };
    }
    return {
      primary: lineContent.slice(0, lastArrowIndex),
      secondary: lineContent.slice(lastArrowIndex + ' → '.length),
    };
  }, [lineContent, isTestPlan]);

  const handleRowClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // Let inline markdown links navigate without flipping the box.
      if ((event.target as HTMLElement).closest('a')) {
        return;
      }
      onToggle(!isChecked);
    },
    [isChecked, onToggle]
  );

  return (
    <div
      className="hover:bg-bg-secondary flex cursor-pointer items-start gap-2 rounded-sm py-1 text-sm transition-colors"
      onClick={handleRowClick}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={isChecked}
        onChange={(event) => onToggle(event.target.checked)}
      />
      <CheckboxBase isSelected={isChecked} className="mt-0.5" />
      <span className="flex flex-col gap-0.5">
        <span className="text-fg-primary leading-snug">
          <InlineMarkdown text={primary} />
        </span>
        {secondary !== null && (
          <span className="text-fg-secondary pl-3 leading-snug">
            <span className="text-fg-tertiary">→ </span>
            <InlineMarkdown text={secondary} />
          </span>
        )}
        {/*
          Always rendered (with a non-breaking-space placeholder when
          empty) so the row's vertical size doesn't change when an item
          is checked or unchecked — the attribution slot is pre-allocated
          so the list doesn't reflow as state toggles.
        */}
        <span aria-hidden={marker === '' || !isChecked} className="text-fg-tertiary text-xs italic leading-snug">
          {marker !== '' && isChecked ? <InlineMarkdown text={marker} /> : <>&nbsp;</>}
        </span>
      </span>
    </div>
  );
};

/**
 * Interactive checklist gate over the `## Prerequisites` and
 * `## Pre-release test plan` sections of the open release draft. Renders
 * the body's literal line content (so the gate reflects exactly what
 * GitHub stores), with an optimistic-intent overlay so clicks land
 * instantly.
 *
 * Aggregate state derivation lives in `computeReleaseChecklistGateState`
 * — pure logic so it's unit-testable without a DOM.
 */
const ReleaseChecklistGate = ({
  prerequisiteLineContents,
  testPlanLineContents,
  prerequisiteCheckedIndices,
  testPlanCheckedIndices,
  prerequisiteCheckedMarkers,
  testPlanCheckedMarkers,
  onStateChange,
  onToggle,
}: IReleaseChecklistGateProps) => {
  // Optimistic intent: keys whose click hasn't yet round-tripped through
  // the mutation + cache write. Cleared once the polled state agrees with
  // the recorded intent.
  const [pendingIntent, setPendingIntent] = React.useState<Map<string, boolean>>(new Map());

  const checkedKeys = React.useMemo(() => {
    const set = new Set<string>();
    for (const i of prerequisiteCheckedIndices) {
      set.add(checklistKey('prereq', i));
    }
    for (const i of testPlanCheckedIndices) {
      set.add(checklistKey('testPlan', i));
    }
    for (const [key, intent] of pendingIntent) {
      if (intent) {
        set.add(key);
      } else {
        set.delete(key);
      }
    }
    return set;
  }, [prerequisiteCheckedIndices, testPlanCheckedIndices, pendingIntent]);

  React.useEffect(() => {
    if (pendingIntent.size === 0) {
      return;
    }
    const polled = new Set<string>();
    for (const i of prerequisiteCheckedIndices) {
      polled.add(checklistKey('prereq', i));
    }
    for (const i of testPlanCheckedIndices) {
      polled.add(checklistKey('testPlan', i));
    }
    let changed = false;
    const next = new Map(pendingIntent);
    for (const [key, intent] of pendingIntent) {
      if (polled.has(key) === intent) {
        next.delete(key);
        changed = true;
      }
    }
    if (changed) {
      setPendingIntent(next);
    }
  }, [prerequisiteCheckedIndices, testPlanCheckedIndices, pendingIntent]);

  React.useEffect(() => {
    onStateChange(
      computeReleaseChecklistGateState(prerequisiteLineContents.length, testPlanLineContents.length, checkedKeys)
    );
  }, [checkedKeys, prerequisiteLineContents.length, testPlanLineContents.length, onStateChange]);

  const handleToggle = React.useCallback(
    (section: ChecklistSection, index: number, isSelected: boolean, lineContent: string) => {
      const key = checklistKey(section === ChecklistSection.Prerequisite ? 'prereq' : 'testPlan', index);
      setPendingIntent((prev) => {
        const next = new Map(prev);
        next.set(key, isSelected);
        return next;
      });
      onToggle(section, index, isSelected, lineContent);
    },
    [onToggle]
  );

  const bothEmpty = prerequisiteLineContents.length === 0 && testPlanLineContents.length === 0;
  if (bothEmpty) {
    // Both empty: render both empty-state messages so the operator sees
    // it's a deliberate empty state, not a bug. Per the issue spec,
    // Publish is enabled immediately in this case (driven by the
    // `allChecked: true` state we emit above).
    return (
      <div className="grid grid-cols-2 gap-x-6 gap-y-5">
        <div className="flex flex-col gap-2">
          <h3 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Prerequisites</h3>
          <p className="text-fg-tertiary text-xs italic">No prerequisites required for this release.</p>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Pre-release test plan</h3>
          <p className="text-fg-tertiary text-xs italic">No frontend-verifiable test plan items for this release.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-5">
      <div className="flex flex-col gap-2">
        <h3 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Prerequisites</h3>
        {prerequisiteLineContents.length === 0 ? (
          <p className="text-fg-tertiary text-xs italic">No prerequisites required for this release.</p>
        ) : (
          <ul className="flex flex-col">
            {prerequisiteLineContents.map((lineContent, index) => {
              const key = checklistKey('prereq', index);
              return (
                <li key={key}>
                  <ChecklistRow
                    lineContent={lineContent}
                    marker={prerequisiteCheckedMarkers[index] ?? ''}
                    isChecked={checkedKeys.has(key)}
                    isTestPlan={false}
                    onToggle={(next) => handleToggle(ChecklistSection.Prerequisite, index, next, lineContent)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Pre-release test plan</h3>
        {testPlanLineContents.length === 0 ? (
          <p className="text-fg-tertiary text-xs italic">No frontend-verifiable test plan items for this release.</p>
        ) : (
          <ul className="flex flex-col">
            {testPlanLineContents.map((lineContent, index) => {
              const key = checklistKey('testPlan', index);
              return (
                <li key={key}>
                  <ChecklistRow
                    lineContent={lineContent}
                    marker={testPlanCheckedMarkers[index] ?? ''}
                    isChecked={checkedKeys.has(key)}
                    isTestPlan
                    onToggle={(next) => handleToggle(ChecklistSection.TestPlan, index, next, lineContent)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ReleaseChecklistGate;
